import { createHttpEndpoint } from '../../utils'

import type { GetCustomerRequestSellerActionsResponse } from './types/seller-actions'
import type { GetCustomerRequestSellerDiscussionResponse } from './types/seller-claim'
import type { GetCareFolderMerchantInformation } from './types/seller-informations'

/**
 * View to retrieve a list of available actions on a care folder (dedicated to merchant)
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/customer-request-internal-api/definition#tag/Merchant/operation/getMerchantActions}
 */
export const getCustomerRequestSellerActions =
  createHttpEndpoint<GetCustomerRequestSellerActionsResponse>({
    method: 'GET',
    operationId: 'fetchSellerAllowedActions',
    path: '/bm/customer_request/:customerRequestId/merchant/actions',
  })

/**
 * View to retrieve the discussion for a care folder (dedicated to merchant)
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/customer-request-internal-api/definition#tag/Merchant/operation/getMerchantDiscussion}
 */
export const getCustomerRequestSellerDiscussion =
  createHttpEndpoint<GetCustomerRequestSellerDiscussionResponse>({
    method: 'GET',
    operationId: 'fetchDiscussionCustomerRequestDiscussion',
    path: '/bm/customer_request/:customerRequestId/merchant/discussion',
  })

/**
 * View to declare a problem on a care folder (dedicated to merchant)
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/customer-request-internal-api/definition#tag/Merchant/operation/postMerchantProblem}
 */
export const postCareFolderMerchantProblem = createHttpEndpoint({
  method: 'POST',
  operationId: 'postCustomerRequestProblem',
  path: '/bm/customer_request/:careFolderId/merchant/problem',
})

/**
 * View to post a message on a care folder (dedicated to merchant)
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/customer-request-internal-api/definition#tag/Merchant/operation/postMerchantMessage}
 */
export const postCareFolderMerchantMessage = createHttpEndpoint({
  method: 'POST',
  operationId: 'postSellerMessageCustomerRequestDiscussion',
  path: '/bm/customer_request/:careFolderId/merchant/message',
  // No timeout for this endpoint because of the upload taking a lot of time.
  timeout: 0,
})

/**
 * Open a care folder (dedicated to merchant).
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/customer-request-internal-api/definition#tag/Merchant/operation/postMerchantOpen}
 */
export const postCareFolderMerchantOpen = createHttpEndpoint({
  method: 'POST',
  operationId: 'openCustomerRequestSeller',
  path: '/bm/customer_request/:customerRequestId/merchant/open',
})

/**
 * View to create a manual product return on a care folder (dedicated to merchant)
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/customer-request-internal-api/definition#tag/Merchant/operation/postMerchantProductReturnManual}
 */
export const postCareFolderMerchantProductReturnsManual = createHttpEndpoint({
  defaultQueryParams: { is_return: 'true' },
  method: 'POST',
  operationId: 'returnBackCustomerRequestPlatform',
  path: '/bm/customer_request/:customerRequestId/merchant/product_returns/manual',
})

/**
 * View to retrieve a list of claims using filter. Return an empty list if no result match with filter.
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/customer-request-internal-api/definition#tag/Merchant/operation/getMerchantClaimsAll}
 */
export const getMerchantAllClaims = createHttpEndpoint({
  method: 'GET',
  operationId: 'fetchCustomerRequestPlatformConversations',
  path: '/bm/customer_request/merchant/claims/all',
})

/**
 * View to retrieve a list of pending claims.
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/customer-request-internal-api/definition#tag/Merchant/operation/getMerchantClaimsPending}
 */
export const getMerchantPendingClaims = createHttpEndpoint({
  method: 'GET',
  operationId: 'fetchCustomerRequestPlatformConversations',
  path: '/bm/customer_request/merchant/claims/pending',
})

/**
 * View to retrieve a list of closed claims.
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/customer-request-internal-api/definition#tag/Merchant/operation/getMerchantClaimsClosed}
 */
export const getMerchantClosedClaims = createHttpEndpoint({
  method: 'GET',
  operationId: 'fetchCustomerRequestPlatformConversations',
  path: '/bm/customer_request/merchant/claims/closed',
})

/**
 * View to retrieve information on merchant (used to build CR platform sidebar).
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/customer-request-internal-api/definition#tag/Merchant/operation/getMerchantInformation}
 */
export const getMerchantInformation =
  createHttpEndpoint<GetCareFolderMerchantInformation.Response>({
    method: 'GET',
    operationId: 'fetchCustomerRequestPlatformMerchant',
    path: '/bm/customer_request/merchant/information',
  })

/**
 * View to retrieve a list of pending returns (i.e. arrived at merchant factory).
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/customer-request-internal-api/definition#tag/Merchant/operation/getMerchantProductReturnsPending}
 */
export const getMerchantPendingProductReturns = createHttpEndpoint({
  method: 'GET',
  operationId: 'fetchPendingProductReturnCustomerRequestPlatform',
  path: '/bm/customer_request/merchant/product_returns/pending',
})

/**
 * View to retrieve counts of care folder related objects.
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/customer-request-internal-api/definition#tag/Merchant/operation/getMerchantCounts}
 */
export const getMerchantCounts = createHttpEndpoint({
  method: 'GET',
  operationId: 'fetchCustomerRequestPlatformCounts',
  path: '/bm/customer_request/merchant/counts',
})

/**
 * View to retrieve a list of invoice requests.
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/customer-request-internal-api/definition#tag/Merchant/operation/getMerchantInvoiceRequests}
 */
export const getMerchantInvoiceRequests = createHttpEndpoint({
  method: 'GET',
  operationId: 'fetchCustomerRequestPlatformInvoiceRequests',
  path: '/bm/customer_request/merchant/invoice_requests',
})

/**
 * Retrieve a list of SavMessageTemplateCategory and SavMessageTemplate.
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/customer-request-internal-api/definition#tag/Merchant/operation/getMerchantTemplates}
 */
export const getMerchantTemplates = createHttpEndpoint({
  method: 'GET',
  operationId: 'fetchCustomerRequestPlatformTemplates',
  path: '/bm/customer_request/merchant/templates',
})

/**
 * Create a new seller request
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/customer-request-internal-api/definition#tag/Merchant/operation/postSellerRequestFromOrderline}
 */
export const postOrderlineSellerRequests = createHttpEndpoint<{
  customerRequestId: string
}>({
  method: 'POST',
  operationId: 'sendMessageToOrderCustomer',
  path: '/bm/customer_request/orderline/:orderLineId/seller_requests',
})

/**
 * Returns a list of SavMessageTemplateCategory
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/customer-request-internal-api/definition#tag/Merchant/operation/getTemplatesCategories}
 */
export const getTemplatesCategories = createHttpEndpoint({
  method: 'GET',
  operationId: 'fetchCategoriesCustomerRequestTemplates',
  path: '/bm/customer_request/templates/categories',
})

/**
 * Creates a SavMessageTemplateCategory
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/customer-request-internal-api/definition#tag/Merchant/operation/postTemplatesCategory}
 */
export const postTemplatesCategory = createHttpEndpoint({
  method: 'POST',
  operationId: 'createCategoryCustomerRequestTemplates',
  path: '/bm/customer_request/templates/category/create',
})

/**
 * Replace a SavMessageTemplateCategory instance
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/customer-request-internal-api/definition#tag/Staff/operation/replaceTemplatesCategory}
 */
export const putTemplatesCategory = createHttpEndpoint({
  method: 'PUT',
  operationId: 'updateCategoryCustomerRequestTemplates',
  path: '/bm/customer_request/templates/category/:categoryId',
})

/**
 * Soft delete a SavMessageTemplateCategory instance
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/customer-request-internal-api/definition#tag/Staff/operation/deleteTemplatesCategory}
 */
export const deleteTemplatesCategory = createHttpEndpoint({
  method: 'DELETE',
  operationId: 'deleteCategoryCustomerRequestTemplates',
  path: '/bm/customer_request/templates/category/:categoryId',
})

/**
 * Returns a list of SavMessageTemplate
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/customer-request-internal-api/definition#tag/Staff/operation/getTemplatesCategoryTemplates}
 */
export const getTemplatesCategoryTemplates = createHttpEndpoint({
  defaultQueryParams: { pagination: 'false' },
  method: 'GET',
  operationId: 'fetchTemplatesByCategoriesCustomerRequestTemplates',
  path: '/bm/customer_request/templates/category/:categoryId/templates',
})

/**
 * Creates a SavMessageTemplate
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/customer-request-internal-api/definition#tag/Staff/operation/postTemplate}
 */
export const postTemplate = createHttpEndpoint({
  method: 'POST',
  operationId: 'createTemplateCustomerRequestTemplates',
  path: '/bm/customer_request/templates/template/create',
})

/**
 * Update a SavMessageTemplate instance
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/customer-request-internal-api/definition#tag/Staff/operation/updateTemplate}
 */
export const patchTemplate = createHttpEndpoint({
  method: 'PATCH',
  operationId: 'updateTemplateCustomerRequestTemplates',
  path: '/bm/customer_request/templates/template/:templateId',
})

/**
 * Soft delete a SavMessageTemplate instance
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/customer-request-internal-api/definition#tag/Staff/operation/deleteTemplate}
 */
export const deleteTemplate = createHttpEndpoint({
  method: 'DELETE',
  operationId: 'deleteTemplateCustomerRequestTemplates',
  path: '/bm/customer_request/templates/template/:templateId',
})

/**
 * TODO Add link to API specs when available
 */
export const getMerchantIssues = createHttpEndpoint({
  method: 'GET',
  operationId: 'fetchCustomerRequestPlatformMerchantIssues',
  path: '/bm/customer_request/merchant/claims/:context/issues',
})

/**
 * View to refund (partially or totally) an orderline
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/customer-request-internal-api/definition#/Merchant/postRefund}
 */
export const postCustomerRequestRefund = createHttpEndpoint({
  method: 'POST',
  operationId: 'refundCustomerRequest',
  path: '/bm/customer_request/:customerRequestId/refund',
})
